import type { HistoryEntry } from '@/model/safe-router-back/HistoryEntry';
import { useRouter } from 'vue-router';

const HISTORY_KEY = '__app_navigation_history__';
const MAX_HISTORY_SIZE = 50;

function getNavigationHistory(): HistoryEntry[] {
  try {
    const stored = sessionStorage.getItem(HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveNavigationHistory(history: HistoryEntry[]): void {
  try {
    const trimmed = history.slice(-MAX_HISTORY_SIZE);
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed));
  } catch {
    // We don't need to handle error if there is cache
  }
}

export function trackNavigation(to: HistoryEntry): void {
  const referrer = document.referrer;
  const isExternalReferrer = referrer && new URL(referrer).origin !== window.location.origin;

  let history = getNavigationHistory();

  // Clear history if coming from external source to start fresh
  if (isExternalReferrer) {
    history = [];
  }

  const lastEntry = history[history.length - 1];

  // Only add if different from the last entry (exact duplicate check)
  if (!lastEntry || lastEntry.path !== to.path || lastEntry.fullPath !== to.fullPath) {
    history.push(to);
    saveNavigationHistory(history);
  }
}

export function useSafeRouterBack() {
  const router = useRouter();

  const safeBack = (fallbackPath: string) => {
    const currentPath = router.currentRoute.value.path;
    const referrer = document.referrer;
    const isExternalReferrer = referrer && new URL(referrer).origin !== window.location.origin;

    if (isExternalReferrer) {
      router.push(fallbackPath);
      return;
    }

    const history = getNavigationHistory();

    if (history.length < 2) {
      router.push(fallbackPath);
      return;
    }

    // Find the first history entry with a different path (going backwards)
    let stepsBack = 1;
    let foundValidRoute = false;

    for (let i = history.length - 2; i >= 0; i--) {
      const entry = history[i];

      if (entry.path !== currentPath) {
        foundValidRoute = true;
        break;
      }

      stepsBack++;
    }

    // Navigate based on findings
    if (foundValidRoute) {
      router.go(-stepsBack);

      // Update history to remove the entries we're skipping
      const newHistory = history.slice(0, history.length - stepsBack);
      saveNavigationHistory(newHistory);
    } else {
      router.push(fallbackPath);
    }
  };

  /**
  * Checks the most recent navigation entries for at least one route
  * whose beforeEnter guard returns exactly true
  */
  const hasSafeRouterBack = async (limit?: number): Promise<boolean> => {
    const history = getNavigationHistory();

    const normalizedLimit =
      typeof limit === 'number' && Number.isFinite(limit) ? Math.max(0, Math.trunc(limit)) : history.length;

    if (!normalizedLimit || history.length <= 1) {
      return false;
    }

    // -1 Exclude the current route from being the last index
    const lastIndexToCheck = Math.max(0, history.length - normalizedLimit - 1);
    const routes = router.getRoutes();
    const currentRoute = routes.find((route) => route.path === history[history.length - 1].routerPath);

    // -2 Exclude the current route from being the initial route to check
    for (let index = history.length - 2; index >= lastIndexToCheck; index--) {
      const routeToCheck = routes.find((route) => route.path === history[index].routerPath);

      if (routeToCheck?.beforeEnter) {
        if (
          // @ts-expect-error Next is expected but we not used on any of the guards functions.
          typeof routeToCheck.beforeEnter === 'function' && await routeToCheck.beforeEnter(routeToCheck, currentRoute) === true ||
          // @ts-expect-error Next is expected but we not used on any of the guards functions.
          Array.isArray(routeToCheck?.beforeEnter) && !routeToCheck?.beforeEnter.some(async (guard) => await guard(routeToCheck, currentRoute) === false)
        ) {
          return true;
        }
      }
    }

    return false;
  };

  return {
    safeBack,
    hasSafeRouterBack,
  };
}
